import type { ReactNode } from 'react';

type Props = {
  filename: string;
  children: ReactNode;
  status?: { dot: string; label: string };
  meta?: string;
  edge?: boolean;
  className?: string;
};

/**
 * Reusable code window — title bar with traffic lights, file tab, optional violet→cyan
 * left edge bar (the senior's prescribed "ambient prism nod"), syntax-highlighted body,
 * status footer.
 */
export default function CodeWindow({
  filename,
  children,
  status = { dot: 'bg-ok', label: 'TS — ready' },
  meta = 'ESM · 14.2 KB gzipped',
  edge = false,
  className = '',
}: Props) {
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-border bg-surface shadow-hair ${className}`}
    >
      {edge && <div className="absolute inset-y-0 left-0 w-[3px] bg-pryma-grad-vert" aria-hidden />}
      {/* title bar */}
      <div className="flex items-center justify-between border-b border-border px-5 py-3">
        <div className="flex items-center gap-2">
          <span className="size-3 rounded-full bg-[#FF5F57]" />
          <span className="size-3 rounded-full bg-[#FFBD2E]" />
          <span className="size-3 rounded-full bg-[#28C940]" />
        </div>
        <div className="flex items-center gap-2 rounded-md bg-slate100 px-2.5 py-1 text-[12px] font-medium text-body">
          <span className="grid size-3.5 place-items-center rounded-sm bg-[#3178C6] text-[8px] font-bold text-white leading-none">
            TS
          </span>
          <span className="font-mono">{filename}</span>
          <span className="size-1.5 rounded-full bg-ok" aria-label="saved" />
        </div>
        <button className="text-[11px] text-muted hover:text-body transition-colors">Copy</button>
      </div>
      {/* body */}
      <div className="bg-codeBg px-5 py-5 font-mono text-[13px] leading-[1.7] text-ink">
        {children}
      </div>
      {/* footer */}
      <div className="flex items-center justify-between border-t border-border bg-slate50 px-5 py-2.5 text-[11px] font-mono">
        <div className="flex items-center gap-2">
          <span className={`size-1.5 rounded-full ${status.dot}`} />
          <span className="font-semibold text-emerald-700 dark:text-emerald-300">
            {status.label}
          </span>
        </div>
        <span className="text-muted">{meta}</span>
      </div>
    </div>
  );
}

/* Tiny helpers for hand-rolled syntax highlight in code bodies — dark-aware. */
export const KW = ({ children }: { children: ReactNode }) => (
  <span className="text-violet font-semibold dark:text-violet-300">{children}</span>
);
export const STR = ({ children }: { children: ReactNode }) => (
  <span className="text-emerald-600 dark:text-emerald-400">{children}</span>
);
export const COM = ({ children }: { children: ReactNode }) => (
  <span className="text-muted italic">{children}</span>
);
export const TYP = ({ children }: { children: ReactNode }) => (
  <span className="text-amber-600 dark:text-amber-300">{children}</span>
);
